/**
 * Responsive utilities for LIEC website
 * Provides consistent breakpoint handling and responsive behavior
 */

export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Get current breakpoint based on window width
 */
export const getCurrentBreakpoint = (): BreakpointKey => {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  if (width < BREAKPOINTS.mobile) return 'mobile';
  if (width < BREAKPOINTS.tablet) return 'tablet';
  return 'desktop';
};

/**
 * Check if current viewport matches a specific breakpoint
 */
export const isBreakpoint = (breakpoint: BreakpointKey): boolean => {
  if (typeof window === 'undefined') return breakpoint === 'desktop';
  
  const width = window.innerWidth;
  switch (breakpoint) {
    case 'mobile':
      return width < BREAKPOINTS.mobile;
    case 'tablet':
      return width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet;
    case 'desktop':
      return width >= BREAKPOINTS.desktop;
    default:
      return false;
  }
};

/**
 * Hook for responsive behavior
 * Uses matchMedia for better performance and cross-browser compatibility
 */
export const useResponsive = () => {
  const [breakpoint, setBreakpoint] = React.useState<BreakpointKey>(getCurrentBreakpoint);
  
  React.useEffect(() => {
    // Use matchMedia for better performance (no resize event spam)
    const mobileQuery = window.matchMedia(`(max-width: ${BREAKPOINTS.mobile - 1}px)`);
    const tabletQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.mobile}px) and (max-width: ${BREAKPOINTS.desktop - 1}px)`);
    
    const handleChange = () => {
      setBreakpoint(getCurrentBreakpoint());
    };
    
    // Modern browsers
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleChange);
      tabletQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older Safari (< 14)
      mobileQuery.addListener(handleChange);
      tabletQuery.addListener(handleChange);
    }
    
    return () => {
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', handleChange);
        tabletQuery.removeEventListener('change', handleChange);
      } else {
        mobileQuery.removeListener(handleChange);
        tabletQuery.removeListener(handleChange);
      }
    };
  }, []);
  
  return {
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop',
    isBreakpoint,
  };
};

/**
 * Touch detection utilities
 */
export const isTouchDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore - for older browsers
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Responsive class utilities
 */
export const getResponsiveClasses = (
  mobile: string,
  tablet?: string,
  desktop?: string
): string => {
  const classes = [mobile];
  
  if (tablet) {
    classes.push(`md:${tablet}`);
  }
  
  if (desktop) {
    classes.push(`lg:${desktop}`);
  }
  
  return classes.join(' ');
};

/**
 * Container classes for consistent responsive behavior
 */
export const RESPONSIVE_CONTAINER_CLASSES = 'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8';

/**
 * Grid classes for responsive layouts
 */
export const getResponsiveGridClasses = (
  mobileColumns: number,
  tabletColumns?: number,
  desktopColumns?: number
): string => {
  const classes = [`grid-cols-${mobileColumns}`];
  
  if (tabletColumns) {
    classes.push(`md:grid-cols-${tabletColumns}`);
  }
  
  if (desktopColumns) {
    classes.push(`lg:grid-cols-${desktopColumns}`);
  }
  
  return `grid ${classes.join(' ')} gap-4 md:gap-6 lg:gap-8`;
};

/**
 * Typography classes for responsive text sizing
 */
export const getResponsiveTextClasses = (
  baseSize: string,
  tabletSize?: string,
  desktopSize?: string
): string => {
  const classes = [baseSize];
  
  if (tabletSize) {
    classes.push(`md:${tabletSize}`);
  }
  
  if (desktopSize) {
    classes.push(`lg:${desktopSize}`);
  }
  
  return classes.join(' ');
};

/**
 * Spacing classes for responsive padding/margin
 */
export const getResponsiveSpacingClasses = (
  property: 'p' | 'm' | 'px' | 'py' | 'pt' | 'pb' | 'pl' | 'pr' | 'mx' | 'my' | 'mt' | 'mb' | 'ml' | 'mr',
  mobileSize: number,
  tabletSize?: number,
  desktopSize?: number
): string => {
  const classes = [`${property}-${mobileSize}`];
  
  if (tabletSize) {
    classes.push(`md:${property}-${tabletSize}`);
  }
  
  if (desktopSize) {
    classes.push(`lg:${property}-${desktopSize}`);
  }
  
  return classes.join(' ');
};

/**
 * Accessibility helpers for touch devices
 */
export const getTouchAccessibilityClasses = (): string => {
  if (typeof window === 'undefined') return '';
  
  return isTouchDevice() 
    ? 'touch-manipulation select-none' 
    : '';
};

/**
 * Focus management for responsive components
 */
export const getFocusClasses = (): string => {
  return 'focus:outline-none focus:ring-2 focus:ring-liec-primary-500 focus:ring-offset-2';
};

/**
 * Smooth transition classes for responsive changes
 */
export const getTransitionClasses = (): string => {
  return 'transition-all duration-300 ease-in-out';
};

// Import React for the hook
import React from 'react';